<script setup>
import { computed } from 'vue'
import draggable from 'vuedraggable'

const props = defineProps({
  leads: { type: Array, default: () => [] },
  statuses: { type: Array, required: true },
})

const emit = defineEmits(['status-change', 'select'])

const columns = computed(() => {
  const map = {}
  for (const status of props.statuses) map[status.value] = []
  for (const lead of props.leads) {
    if (map[lead.status]) map[lead.status].push(lead)
  }
  return map
})

function onChange(status, evt) {
  if (evt.added) {
    emit('status-change', { lead: evt.added.element, newStatus: status })
  }
}
</script>

<template>
  <div class="kanban-board">
    <div v-for="column in statuses" :key="column.value" class="kanban-column">
      <div class="kanban-column-header">
        <v-icon size="16">{{ column.icon }}</v-icon>
        <span>{{ column.label }}</span>
        <span class="kanban-count">{{ columns[column.value].length }}</span>
      </div>

      <draggable
        :list="columns[column.value]"
        group="leads"
        item-key="id"
        class="kanban-column-body"
        ghost-class="kanban-ghost"
        @change="(evt) => onChange(column.value, evt)"
      >
        <template #item="{ element }">
          <button
            type="button"
            class="kanban-card"
            @click="emit('select', element.id)"
          >
            <span class="kanban-card-avatar">
              {{ (element.contact_name || '?').charAt(0) }}
            </span>
            <span class="kanban-card-body">
              <strong>{{ element.contact_name }}</strong>
              <span>{{ element.service || 'General inquiry' }}</span>
            </span>
          </button>
        </template>
      </draggable>
    </div>
  </div>
</template>

<style scoped>
.kanban-board {
  height: 100%;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(220px, 1fr);
  gap: 12px;
  padding: 4px;
  overflow-x: auto;
}

.kanban-column {
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  overflow: hidden;
}

.kanban-column-header {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 12px 12px;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--surface);
  color: var(--text-primary);
  font-size: 0.84rem;
  font-weight: 800;
}

.kanban-count {
  margin-left: auto;
  color: var(--text-secondary);
  font-weight: 700;
}

.kanban-column-body {
  min-height: 80px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  overflow-y: auto;
}

.kanban-card {
  width: 100%;
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  align-items: center;
  gap: 9px;
  padding: 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface);
  text-align: left;
  cursor: grab;
  box-shadow: 0 4px 10px rgba(23, 35, 31, 0.05);
}

.kanban-card-avatar {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: var(--surface-alt);
  color: var(--text-primary);
  font-weight: 850;
  font-size: 0.84rem;
}

.kanban-card-body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.kanban-card-body strong {
  overflow: hidden;
  color: var(--text-primary);
  font-size: 0.86rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kanban-card-body span {
  overflow: hidden;
  color: var(--text-secondary);
  font-size: 0.76rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kanban-ghost {
  opacity: 0.4;
}
</style>
